import { lotusHealthSchemas, lotusHealthZod } from '@lotushealth/lotushealth_js_sdk';
import { z } from 'zod';

import type { CompoundIngredientDraft, RxDraft, RxRequest } from './types.js';

function numberFromString(value: string): number {
  return Number(value.trim());
}

/**
 * Normalize Unicode characters to printable ASCII for NCPDP SCRIPT-safe
 * pharmacy transmission.
 */
export function sanitizeRxText(text: string): string {
  return (
    text
      .replace(/[\u2013\u2014\u2212]/g, '-')
      .replace(/[\u2018\u2019\u02BC]/g, "'")
      .replace(/[\u201C\u201D]/g, '"')
      .replace(/[\u2026]/g, '...')
      .replace(/[\u00A0\u2007\u202F]/g, ' ')
      .replace(/\u00B0/g, ' degrees ')
      .replace(/\u00BD/g, '1/2')
      .replace(/\u00BC/g, '1/4')
      .replace(/\u00BE/g, '3/4')
      .replace(/[\u2264]/g, '<=')
      .replace(/[\u2265]/g, '>=')
      .replace(/[\r\n]+/g, ' ')
      // Strip anything outside printable ASCII (0x20–0x7E)
      .replace(/[^\x20-\x7E]/g, '')
      .replace(/\s{2,}/g, ' ')
      .trim()
  );
}

type MedicationStatus = lotusHealthSchemas.MedicationStatusTInput;
const medicationStatusValues = Object.values(
  lotusHealthSchemas.MedicationStatusTInput
) as MedicationStatus[];
const rxMedicationStatusSchema = z.enum(
  medicationStatusValues as [MedicationStatus, ...MedicationStatus[]]
);

const medicationSchema = z
  .object({
    DispensableDrugId: z.number().int().positive(),
  })
  .catchall(z.unknown());

const pharmacySchema = z
  .object({
    PharmacyId: z.number().int().positive(),
  })
  .catchall(z.unknown());

const commonDraftFields = {
  quantity: z.string().trim().min(1, 'Quantity is required'),
  dispenseUnitId: z.string().trim().min(1, 'Dispense unit is required'),
  directions: z
    .string()
    .trim()
    .min(1, 'Directions are required')
    .max(1000, 'Directions must be 1000 characters or less'),
  refills: z
    .string()
    .trim()
    .min(1, 'Refills is required')
    .transform((value) => Number(value))
    .pipe(z.int().min(0, 'Refills must be 0 or greater').max(5, 'Refills must be 5 or less'))
    .transform((value) => String(value)),
  daysSupply: z.string().trim().min(1, 'Days supply is required'),
  selectedPharmacy: pharmacySchema.nullable(),
  pharmacyNotes: z.string().trim().max(210, 'Pharmacy notes must be 210 characters or less'),
  noSubstitutions: z.boolean(),
  effectiveDate: z
    .string()
    .trim()
    .min(1, 'Effective date is required')
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Effective date must be in YYYY-MM-DD format'),
  isUrgent: z.boolean(),
  status: rxMedicationStatusSchema,
  comment: z.string().trim(),
  selectedDiagnosis: z
    .object({
      diagnosisId: z.number().int().positive(),
      code: z.string(),
      name: z.string(),
    })
    .nullable(),
} as const;

const codedDraftSchema = z.object({
  kind: z.literal('coded'),
  selectedMedication: medicationSchema.nullable(),
  ...commonDraftFields,
});

const ingredientDraftSchema: z.ZodType<CompoundIngredientDraft> = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('coded'),
    DispensableDrugId: z.number().int().positive(),
    FreeText: z.string().optional(),
    DispenseUnitId: z.number().int().positive().optional(),
    Quantity: z.string().trim().min(1, 'Ingredient quantity is required'),
    IsFreeTextEPCS: z.boolean().optional(),
  }),
  z.object({
    kind: z.literal('freeText'),
    FreeText: z.string().trim().min(1, 'Ingredient name is required'),
    DispenseUnitId: z.number().int().positive().optional(),
    Quantity: z.string().trim().min(1, 'Ingredient quantity is required'),
    IsFreeTextEPCS: z.boolean().optional(),
  }),
]);

const compoundDraftSchema = z.object({
  kind: z.literal('compound'),
  compoundDescription: z.string().trim().min(1, 'Compound description is required'),
  ingredients: z.array(ingredientDraftSchema).min(1, 'At least one ingredient is required'),
  ...commonDraftFields,
});

/**
 * Validates the UI draft (string inputs + selected objects) for coded/compound Rxs.
 * This is the only validation gate required for moving Details -> Review.
 */
export const rxDraftSchema: z.ZodType<RxDraft> = z
  .discriminatedUnion('kind', [codedDraftSchema, compoundDraftSchema])
  .superRefine((draft, ctx) => {
    // Required selections
    if (draft.kind === 'coded' && !draft.selectedMedication) {
      ctx.addIssue({
        code: 'custom',
        path: ['selectedMedication'],
        message: 'Please select a medication',
      });
    }

    if (!draft.selectedPharmacy) {
      ctx.addIssue({
        code: 'custom',
        path: ['selectedPharmacy'],
        message: 'Pharmacy selection is required',
      });
    }

    // Numeric constraints
    const quantity = numberFromString(draft.quantity);
    if (!Number.isFinite(quantity) || quantity <= 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['quantity'],
        message: 'Quantity must be a number greater than 0',
      });
    }

    const dispenseUnitId = numberFromString(draft.dispenseUnitId);
    if (!Number.isFinite(dispenseUnitId) || dispenseUnitId < 1) {
      ctx.addIssue({
        code: 'custom',
        path: ['dispenseUnitId'],
        message: 'Dispense unit is required',
      });
    }

    const daysSupply = numberFromString(draft.daysSupply);
    if (!Number.isFinite(daysSupply) || daysSupply <= 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['daysSupply'],
        message: 'Days supply must be a number greater than 0',
      });
    }
  });

/**
 * Validates and transforms an Rx draft into the exact request object required by the API schema.
 * This is the only gate required for submit.
 */
export const rxRequestFromDraftSchema = rxDraftSchema
  .transform((draft): RxRequest => {
    const base = {
      Quantity: numberFromString(draft.quantity),
      DispenseUnitId: numberFromString(draft.dispenseUnitId),
      Directions: sanitizeRxText(draft.directions),
      Refills: numberFromString(draft.refills),
      PharmacyId: draft.selectedPharmacy?.PharmacyId ?? 0,
      PharmacyNotes: sanitizeRxText(draft.pharmacyNotes) || undefined,
      NoSubstitutions: draft.noSubstitutions,
      EffectiveDate: draft.effectiveDate.trim(),
      IsUrgent: draft.isUrgent,
      Status: draft.status,
      Comment: draft.comment.trim() || undefined,
      DaysSupply: numberFromString(draft.daysSupply),
    } as const;

    if (draft.kind === 'coded') {
      return {
        DispensableDrugId: draft.selectedMedication?.DispensableDrugId ?? 0,
        ...base,
      };
    }

    return {
      CompoundDescription: draft.compoundDescription.trim(),
      CompoundIngredients: draft.ingredients.map((ingredient) => {
        if (ingredient.kind === 'coded') {
          return {
            DispensableDrugId: ingredient.DispensableDrugId,
            Quantity: ingredient.Quantity,
            ...(ingredient.DispenseUnitId !== undefined
              ? { DispenseUnitId: ingredient.DispenseUnitId }
              : {}),
            ...(ingredient.IsFreeTextEPCS !== undefined
              ? { IsFreeTextEPCS: ingredient.IsFreeTextEPCS }
              : {}),
          };
        }

        return {
          FreeText: ingredient.FreeText.trim(),
          Quantity: ingredient.Quantity,
          ...(ingredient.DispenseUnitId !== undefined
            ? { DispenseUnitId: ingredient.DispenseUnitId }
            : {}),
          ...(ingredient.IsFreeTextEPCS !== undefined
            ? { IsFreeTextEPCS: ingredient.IsFreeTextEPCS }
            : {}),
        };
      }),
      ...base,
    };
  })
  .superRefine((request, ctx) => {
    const result = lotusHealthZod.createPrescriptionBody.shape.prescriptionData.safeParse(request);
    if (result.success) return;
    ctx.addIssue({
      code: 'custom',
      path: ['_form'],
      message: 'Internal validation failed: request does not match API schema',
    });
  });

export const rxDraftToolInputSchema = z.object({
  dispensableDrugId: z.number().int().positive(),
  directions: commonDraftFields.directions,
  quantity: z.number().positive(),
  refills: z.number().int().min(0).max(5),
  daysSupply: z.number().positive(),
  pharmacyId: z.number().int().positive().optional(),
  pharmacyNotes: commonDraftFields.pharmacyNotes.optional(),
  noSubstitutions: commonDraftFields.noSubstitutions.optional(),
});
